import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import StatusBadge from "../components/StatusBadge";
import { ArrowLeft, Loader2, IndianRupee, Package, Phone, Mail, Calendar, AlertCircle, CheckCircle2, Wallet, Building2, Download, ExternalLink } from "lucide-react";
import { downloadInvoice } from "../utils/invoiceDownload";

export default function AdminCustomerDetail() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [p, o, pay] = await Promise.all([
          api.get(`/admin/customers/${customerId}`),
          api.get(`/admin/customers/${customerId}/orders`),
          api.get(`/admin/customers/${customerId}/payments?limit=50`),
        ]);
        if (!live) return;
        setProfile(p.data);
        setOrders(o.data);
        setPayments(pay.data);
      } catch (err) {
        if (live) setError(err.response?.data?.detail || "Failed to load customer");
      } finally {
        if (live) setLoading(false);
      }
    };
    load();
    return () => { live = false; };
  }, [customerId]);

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="customer-detail-loading">
      <Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" />
    </div>
  );
  if (error) return (
    <div className="p-10 text-center" data-testid="customer-detail-error">
      <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
      <p className="text-base font-semibold text-slate-700">Couldn't load customer</p>
      <p className="text-sm text-slate-400 mt-1">{error}</p>
      <Button onClick={() => navigate(-1)} className="mt-4 rounded-xl"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
    </div>
  );

  const { customer, stats } = profile;
  const orFallback = (v) => (v === undefined || v === null || v === "" ? "N/A" : v);

  return (
    <div className="space-y-6 animate-fadeInUp" data-testid="customer-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate("/dashboard/customers")} className="rounded-xl" data-testid="customer-detail-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Customers
          </Button>
          <div>
            <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900" data-testid="customer-detail-name">{customer.name}</h1>
            <p className="text-sm text-slate-500 mt-0.5">Customer profile, orders & payments</p>
          </div>
        </div>
        {customer.is_b2b && (
          <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold">
            <Building2 className="h-3.5 w-3.5" /> B2B Account
          </span>
        )}
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6" data-testid="customer-profile-card">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center shrink-0">
            <span className="text-blue-700 text-2xl font-bold font-['Outfit']">{(customer.name || "C")[0].toUpperCase()}</span>
          </div>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <InfoRow icon={Mail} label="Email" value={orFallback(customer.email)} />
            <InfoRow icon={Phone} label="Phone" value={orFallback(customer.phone)} />
            <InfoRow icon={Calendar} label="Joined" value={customer.created_at ? new Date(customer.created_at).toLocaleDateString() : "N/A"} />
            {customer.is_b2b && <InfoRow icon={Building2} label="Company" value={orFallback(customer.company_name)} />}
            <InfoRow icon={Wallet} label="Wallet" value={`₹${stats.wallet_balance ?? 0}`} />
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Package} label="Total Orders" value={stats.total_orders} color="blue" testid="kpi-total-orders" />
        <KpiCard icon={CheckCircle2} label="Delivered" value={stats.delivered_orders} color="green" testid="kpi-delivered" />
        <KpiCard icon={AlertCircle} label="Cancelled" value={stats.cancelled_orders} color="red" testid="kpi-cancelled" />
        <KpiCard icon={Package} label="In Progress" value={stats.in_progress_orders} color="orange" testid="kpi-in-progress" />
        <KpiCard icon={IndianRupee} label="Total Paid" value={`₹${stats.total_paid}`} color="emerald" testid="kpi-total-paid" />
        <KpiCard icon={IndianRupee} label="Pending Payment" value={`₹${stats.pending_payment}`} color="amber" testid="kpi-pending-payment" sub={`${stats.pending_payment_orders} orders`} />
        <KpiCard icon={Wallet} label="Wallet" value={`₹${stats.wallet_balance ?? 0}`} color="slate" testid="kpi-wallet" />
        <KpiCard icon={Package} label="Paid Orders" value={stats.paid_orders} color="emerald" testid="kpi-paid-orders" />
      </div>

      {/* Orders */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm" data-testid="customer-orders-section">
        <div className="p-5 border-b border-slate-100">
          <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">Order History</h2>
          <p className="text-xs text-slate-400 mt-0.5">{orders.length} orders</p>
        </div>
        {orders.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">No orders yet</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-100 bg-slate-50/60">
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 pl-6">Tracking</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Status</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Item</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Total</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Date</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.slice(0, 50).map((o) => (
                  <TableRow key={o.tracking_id} className="border-slate-50 row-hover" data-testid={`customer-order-${o.tracking_id}`}>
                    <TableCell className="pl-6 py-3"><span className="font-mono text-sm font-semibold text-slate-900">{o.tracking_id}</span></TableCell>
                    <TableCell className="py-3"><StatusBadge status={o.status} /></TableCell>
                    <TableCell className="py-3 text-sm text-slate-700">{orFallback(o.package_description)}</TableCell>
                    <TableCell className="py-3 text-sm text-slate-700"><IndianRupee className="h-3 w-3 inline" />{(o.price?.final_price ?? o.price?.total) || "—"}</TableCell>
                    <TableCell className="py-3 text-xs text-slate-500">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "N/A"}</TableCell>
                    <TableCell className="py-3">
                      <div className="flex items-center gap-1.5">
                        <a
                          href={`/dashboard/orders?tracking=${o.tracking_id}`}
                          onClick={(e) => { e.preventDefault(); navigate(`/dashboard/orders`); }}
                          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[10px] font-semibold border border-slate-200 text-slate-600 hover:border-[#FF6B00] hover:text-[#FF6B00]"
                          data-testid={`view-order-${o.tracking_id}`}
                        >
                          <ExternalLink className="h-3 w-3" /> View
                        </a>
                        <button
                          type="button"
                          onClick={() => downloadInvoice(o.tracking_id)}
                          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[10px] font-semibold border border-slate-200 text-slate-600 hover:border-[#FF6B00] hover:text-[#FF6B00]"
                          data-testid={`invoice-${o.tracking_id}`}
                        >
                          <Download className="h-3 w-3" /> Invoice
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Payments */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm" data-testid="customer-payments-section">
        <div className="p-5 border-b border-slate-100">
          <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">Payment History</h2>
          <p className="text-xs text-slate-400 mt-0.5">{payments.length} entries</p>
        </div>
        {payments.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">No payments yet</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-100 bg-slate-50/60">
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 pl-6">Order</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Method</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Status</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Razorpay ID</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Amount</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.slice(0, 50).map((p) => (
                  <TableRow key={p.tracking_id} className="border-slate-50 row-hover" data-testid={`customer-payment-${p.tracking_id}`}>
                    <TableCell className="pl-6 py-3"><span className="font-mono text-sm font-semibold text-slate-900">{p.tracking_id}</span></TableCell>
                    <TableCell className="py-3">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${
                        p.payment_method === "cod" ? "bg-amber-50 text-amber-700 border-amber-200"
                        : p.payment_method === "wallet" ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                        : "bg-blue-50 text-blue-700 border-blue-200"
                      }`}>{(p.payment_method || "N/A").toUpperCase()}</span>
                    </TableCell>
                    <TableCell className="py-3">
                      {p.payment_status === "paid"
                        ? <span className="text-[11px] font-semibold text-emerald-600">Paid</span>
                        : <span className="text-[11px] font-semibold text-amber-600">{(p.payment_status || "pending").toUpperCase()}</span>}
                    </TableCell>
                    <TableCell className="py-3 text-xs text-slate-500 font-mono">{orFallback(p.razorpay_payment_id)}</TableCell>
                    <TableCell className="py-3 text-sm font-semibold text-slate-900"><IndianRupee className="h-3 w-3 inline" />{(p.price?.final_price ?? p.price?.total) || "—"}</TableCell>
                    <TableCell className="py-3 text-xs text-slate-500">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "N/A"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-slate-400 shrink-0" />
      <div className="min-w-0">
        <p className="text-[11px] text-slate-400 uppercase tracking-wider">{label}</p>
        <p className="text-sm font-semibold text-slate-900 truncate">{value}</p>
      </div>
    </div>
  );
}

const kpiTone = {
  blue: "bg-blue-50 text-blue-700",
  green: "bg-green-50 text-green-700",
  orange: "bg-orange-50 text-[#FF6B00]",
  red: "bg-red-50 text-red-600",
  emerald: "bg-emerald-50 text-emerald-700",
  amber: "bg-amber-50 text-amber-700",
  slate: "bg-slate-100 text-slate-600",
};

function KpiCard({ icon: Icon, label, value, color = "slate", testid, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 card-hover" data-testid={testid}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${kpiTone[color]}`}>
        <Icon className="h-4 w-4" />
      </div>
      <p className="font-['Outfit'] text-xl font-bold text-slate-900 tracking-tight">{value}</p>
      <p className="text-xs text-slate-500 mt-0.5">{label}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}
